import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

/**
 * BaseCard — visual foundation for every content card on the site.
 * Cards are always <Link>s (with optional image, eyebrow, title, blurb, footer).
 *
 * Props:
 *  - to: string                        internal route
 *  - image?: string                    photo src (from /public)
 *  - imagePosition?: string            CSS background-position, default "center"
 *  - eyebrow?: string                  small uppercase label above title
 *  - title: string                     card headline
 *  - blurb?: string                    supporting text
 *  - footer?: ReactNode                extra footer content (tags, meta)
 *  - variant?: "photo" | "flat"        default "flat"
 *  - testid?: string
 */
const BaseCard = ({
  to,
  image,
  imagePosition = "center",
  eyebrow,
  title,
  blurb,
  footer,
  variant = "flat",
  testid,
}) => {
  const isPhoto = variant === "photo" && image;

  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block rounded-xl overflow-hidden border transition-all relative"
      style={{
        borderColor: "var(--lt-border-strong)",
        background:
          "linear-gradient(180deg, rgba(15, 26, 51, 0.55) 0%, rgba(6, 11, 24, 0.75) 100%)",
      }}
    >
      {isPhoto && (
        <div
          className="aspect-[16/10] w-full overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(6,11,24,0.15) 0%, rgba(6,11,24,0.55) 100%), url('${image}')`,
            backgroundSize: "cover",
            backgroundPosition: imagePosition,
          }}
          aria-hidden="true"
        />
      )}
      <div className="p-5">
        {eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[color:var(--lt-blue-soft)]">
            {eyebrow}
          </p>
        )}
        <div className="flex items-start justify-between gap-3 mt-2">
          <h3
            className="font-display text-white leading-tight"
            style={{ fontSize: 20, letterSpacing: "0.01em" }}
          >
            {title}
          </h3>
          <ArrowUpRight
            size={18}
            className="text-[color:var(--lt-blue-soft)] flex-shrink-0 mt-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            aria-hidden="true"
          />
        </div>
        {blurb && (
          <p className="mt-3 text-[13.5px] leading-relaxed text-[color:var(--lt-text-dim)]">
            {blurb}
          </p>
        )}
        {footer && <div className="mt-4">{footer}</div>}
      </div>
    </Link>
  );
};

export default BaseCard;
